import { useState, useCallback, useMemo } from 'react';
import { useHaptics } from '@/hooks/useHaptics';

interface Identifiable {
  readonly id: string;
}

interface MultiSelectState {
  readonly isSelectMode: boolean;
  readonly selectedItems: Set<string>;
  readonly selectedCount: number;
  readonly isAllSelected: boolean;
}

interface MultiSelectActions {
  readonly setIsSelectMode: (mode: boolean) => void;
  readonly toggleItemSelection: (id: string) => void;
  readonly selectAll: () => void;
  readonly deselectAll: () => void;
  readonly exitSelectMode: () => void;
  readonly handleLongPressSelect: (id: string) => void;
  readonly isSelected: (id: string) => boolean;
}

export type UseMultiSelectReturn = MultiSelectState & MultiSelectActions;

export function useMultiSelect<T extends Identifiable>(items: readonly T[]): UseMultiSelectReturn {
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const haptics = useHaptics();

  const toggleItemSelection = useCallback((id: string) => {
    haptics.light();
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [haptics]);

  const selectAll = useCallback(() => {
    haptics.light();
    setSelectedItems(new Set(items.map(i => i.id)));
  }, [items, haptics]);

  const deselectAll = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedItems(new Set());
  }, []);

  const handleLongPressSelect = useCallback((id: string) => {
    haptics.light();
    setIsSelectMode(true);
    setSelectedItems(new Set([id]));
  }, [haptics]);

  const isSelected = useCallback((id: string): boolean => {
    return selectedItems.has(id);
  }, [selectedItems]);

  const selectedCount = useMemo(() => selectedItems.size, [selectedItems]);
  
  const isAllSelected = useMemo(
    () => items.length > 0 && selectedItems.size === items.length,
    [items.length, selectedItems.size]
  );

  return {
    isSelectMode,
    setIsSelectMode,
    selectedItems,
    selectedCount,
    isAllSelected,
    toggleItemSelection,
    selectAll,
    deselectAll,
    exitSelectMode,
    handleLongPressSelect,
    isSelected,
  };
}
